import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FileWatermark, {
  FileWatermarkProps,
} from 'components/FileWatermark/FileWatermark.component';

import './AutodeskViewer.styles.scss';

const AUTODESK_VIEWER_URL =
  'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*';

let viewerScriptPromise: Promise<void> | null = null;
let viewerStyle: HTMLLinkElement | null = null;

// Stylesheet doesn't block the viewer; a failed one is dropped so the next preview retries it
const loadViewerStyle = () => {
  if (viewerStyle) return;

  viewerStyle = document.createElement('link');
  viewerStyle.rel = 'stylesheet';
  viewerStyle.href = `${AUTODESK_VIEWER_URL}/style.min.css`;
  viewerStyle.onerror = () => {
    viewerStyle.remove();
    viewerStyle = null;
  };
  document.head.appendChild(viewerStyle);
};

const loadViewerScript = () => {
  if (viewerScriptPromise) return viewerScriptPromise;

  viewerScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${AUTODESK_VIEWER_URL}/viewer3D.min.js`;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      viewerScriptPromise = null;
      reject(new Error('Autodesk viewer failed to load'));
    };
    document.head.appendChild(script);
  });

  return viewerScriptPromise;
};

// Viewer is ~1 MB gzipped, so it's injected on first CAD preview instead of in index.html
const loadAutodeskViewer = () => {
  loadViewerStyle();

  return loadViewerScript();
};

type AutodeskViewerProps = {
  className?: string;
  urn: string;
  accessToken: string;
  onDocumentLoad?: () => void;
} & FileWatermarkProps;

const AutodeskViewer: React.FC<AutodeskViewerProps> = (props) => {
  const {
    className,
    urn,
    accessToken,
    fullName,
    email,
    ipAddress,
    watermarkSettings,
    onDocumentLoad,
  } = props;

  const [isLoaded, setIsLoaded] = useState(false);

  const { popError } = useContext(PopNotificationsContext);

  const classes = classNames('autodesk-viewer', className);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Autodesk.Viewing.GuiViewer3D | null>(null);

  const handleDocumentReady = useCallback(() => {
    setIsLoaded(true);
    onDocumentLoad?.();
  }, [onDocumentLoad]);

  useEffect(() => {
    if (!containerRef.current || !urn || !accessToken) return;

    let cancelled = false;

    const initViewer = () => {
      const options: Autodesk.Viewing.InitializerOptions = {
        env: 'AutodeskProduction2',
        api: 'streamingV2',
        getAccessToken: (onSuccess) => {
          onSuccess(accessToken, 3600);
        },
      };

      Autodesk.Viewing.Initializer(options, () => {
        if (cancelled || !containerRef.current) return;

        const viewer = new Autodesk.Viewing.GuiViewer3D(containerRef.current);
        viewer.start();
        viewerRef.current = viewer;

        Autodesk.Viewing.Document.load(
          `urn:${urn}`,
          (doc) => {
            if (cancelled) return;

            const root = doc.getRoot();
            const viewables3d = root.search({ role: '3d' });
            const viewable =
              viewables3d.length > 0
                ? viewables3d[0]
                : root.getDefaultGeometry();
            viewer.loadDocumentNode(doc, viewable);
            handleDocumentReady();
          },
          (_errorCode, _errorMsg) => {
            popError(`Autodesk document load failed: ${_errorMsg}`);
          },
        );
      });
    };

    loadAutodeskViewer()
      .then(() => {
        if (!cancelled) initViewer();
      })
      .catch((error: Error) => {
        if (!cancelled) popError(error.message);
      });

    return () => {
      cancelled = true;

      if (viewerRef.current) {
        viewerRef.current.finish();
        viewerRef.current = null;
      }
    };
  }, [urn, accessToken, popError, handleDocumentReady]);

  return (
    <div className={classes} ref={containerRef}>
      {isLoaded && (
        <FileWatermark
          fullName={fullName}
          email={email}
          ipAddress={ipAddress}
          watermarkSettings={watermarkSettings}
        />
      )}
    </div>
  );
};

export default AutodeskViewer;
